// src/components/BacktestView.js
import React, { useState } from "react";
import { runBacktest, runWalkForward } from "../api/backtest";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
} from "@mui/material";
import ResponsiveContainer from "./ResponsiveContainer";

function prettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "<unserializable payload>";
  }
}

function fmt(v, digits = 2, prefix = "") {
  if (v === null || v === undefined || isNaN(Number(v))) return "--";
  return `${prefix}${Number(v).toFixed(digits)}`;
}

function pctFmt(v) {
  if (v === null || v === undefined || isNaN(Number(v))) return "--";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

function MetricCard({ label, value, tooltip, highlight }) {
  const card = (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, flex: 1, minWidth: 120, borderLeft: highlight ? `4px solid ${highlight}` : undefined }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.25, fontSize: { xs: 15, sm: 18 } }}>
        {value}
      </Typography>
    </Paper>
  );
  return tooltip ? <Tooltip title={tooltip}>{card}</Tooltip> : card;
}

function buildRecentSampleEquity() {
  const today = new Date();
  const rows = ["date,equity"];
  let equity = 100000;
  for (let i = 59; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const drift = 0.0008;
    const wave = Math.sin((60 - i) / 6) * 0.002;
    equity = equity * (1 + drift + wave);
    rows.push(`${iso},${Math.round(equity)}`);
  }
  return rows.join("\n");
}

function parseEquityCSV(text) {
  const lines = text.trim().split("\n").filter(Boolean);
  const rows = [];
  // Parse CSV rows into objects
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(",");
    if (parts.length >= 2) {
      const date = parts[0].trim();
      const equity = parseFloat(parts[1].trim());
      if (date && !isNaN(equity)) rows.push({ date, equity });
    }
  }
  return rows;
}

function filterEquityRowsByDateRange(rows, rangeKey) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const key = String(rangeKey || "all").toLowerCase();
  if (key === "all") return safeRows;

  const monthsByKey = {
    "3m": 3,
    "6m": 6,
    "12m": 12,
  };
  const months = monthsByKey[key];
  if (!months) return safeRows;

  const withDates = safeRows
    .map((r) => ({ ...r, _dateObj: new Date(`${r.date}T00:00:00`) }))
    .filter((r) => !Number.isNaN(r._dateObj.getTime()))
    .sort((a, b) => a._dateObj - b._dateObj);

  if (withDates.length === 0) return safeRows;

  const maxDate = withDates[withDates.length - 1]._dateObj;
  const minDate = new Date(maxDate);
  minDate.setMonth(minDate.getMonth() - months);

  return withDates
    .filter((r) => r._dateObj >= minDate)
    .map(({ _dateObj, ...rest }) => rest);
}

const BacktestView = () => {
  // State for date range filtering
  const [csvText, setCsvText] = useState(() => buildRecentSampleEquity());
  const [windowCount, setWindowCount] = useState(4);
  const [benchmarkName, setBenchmarkName] = useState("SPY");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [result, setResult] = useState(null);
  const [wfResult, setWfResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showDebugPayload, setShowDebugPayload] = useState(false);

  const toPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
    return Number(value) * 100;
  };

  const handleRun = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    const parsedRows = parseEquityCSV(csvText);
    const equityRows = filterEquityRowsByDateRange(parsedRows, dateRange);
    if (equityRows.length < 3) {
      setError("Need at least 3 data points (date,equity rows) after date-range filtering.");
      return;
    }
    setLoading(true);
    setResult(null);
    setWfResult(null);
    try {
      const [btResult, wfData] = await Promise.all([
        runBacktest({
          equity_rows: equityRows,
          benchmark_name: benchmarkName.trim() || "SPY",
          auto_fetch_benchmark: true,
          window_count: Number(windowCount),
        }),
        runWalkForward({
          equity_rows: equityRows,
          benchmark_name: benchmarkName.trim() || "SPY",
          auto_fetch_benchmark: true,
          window_count: Number(windowCount),
        }),
      ]);
      setResult(btResult);
      setWfResult(wfData);
      if (btResult?.benchmark_fetch?.attempted && !btResult?.benchmark_fetch?.used) {
        setInfo("Benchmark fetch returned no rows for the selected symbol/date range. Comparison may be limited.");
      }
    } catch (err) {
      setError(err.message || "Backtest failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleImportCsvFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "").trim();
      if (!text) {
        setError("Imported file is empty.");
        return;
      }
      setCsvText(text);
      setError("");
      setInfo(`Imported CSV file: ${file.name}`);
    };
    reader.onerror = () => {
      setError("Could not read the selected CSV file.");
    };
    reader.readAsText(file);

    // Allow re-selecting the same file in subsequent imports.
    event.target.value = "";
  };

  const runReport = result?.walk_forward_report ?? result?.report ?? result ?? {};
  const wfReport = wfResult?.report ?? wfResult?.walk_forward_report ?? wfResult ?? {};
  const activeReport = Array.isArray(wfReport?.windows) && wfReport.windows.length > 0 ? wfReport : runReport;

  const metrics = activeReport?.summary ?? result?.metrics ?? {};
  const stability = activeReport?.stability ?? {};
  const comparison = result?.benchmark_comparison ?? result?.comparison ?? null;
  const windows = activeReport?.windows ?? [];

  const comparisonBenchmarkReturnPct = comparison?.benchmark_return_pct
    ?? toPercent(comparison?.benchmark?.total_return);
  const comparisonStrategyReturnPct = comparison?.strategy_return_pct
    ?? toPercent(comparison?.strategy?.total_return);
  const comparisonAlphaPct = comparison?.alpha_pct
    ?? toPercent(comparison?.relative?.excess_total_return);
  const comparisonSharpeDiff = comparison?.information_ratio
    ?? comparison?.relative?.sharpe_advantage;
  const comparisonDrawdownAdvantagePct = toPercent(comparison?.relative?.drawdown_advantage);
  const comparisonCagrDiffPct = toPercent(comparison?.relative?.excess_cagr);

  const totalReturn = metrics.total_return_pct ?? toPercent(metrics.total_return);
  const maxDD = metrics.max_drawdown_pct ?? (metrics.max_drawdown !== undefined ? Math.abs(toPercent(metrics.max_drawdown) ?? 0) : null);
  const sharpe = metrics.sharpe_ratio;
  const calmar = metrics.calmar_ratio
    ?? ((metrics.cagr !== undefined && metrics.max_drawdown !== undefined && Math.abs(Number(metrics.max_drawdown)) > 0)
      ? Number(metrics.cagr) / Math.abs(Number(metrics.max_drawdown))
      : null);
  const winRate = metrics.win_rate_pct ?? (stability.consistency_ratio !== undefined ? toPercent(stability.consistency_ratio) : null);
  const volatility = metrics.annualized_volatility_pct ?? toPercent(metrics.volatility);

  const parsedRowsCount = parseEquityCSV(csvText).length;
  const filteredRowsCount = filterEquityRowsByDateRange(parseEquityCSV(csvText), dateRange).length;
  const minWindowObservations = windows.length > 0
    ? Math.min(...windows.map((w) => Number(w.observations ?? w.oos_observations ?? 0) || 0))
    : 0;
  const lowObservationWarning = result && (
    filteredRowsCount < 30 || (windows.length > 0 && minWindowObservations < 5)
  );

  return (
    <ResponsiveContainer>
      <Card elevation={3} sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: 22, sm: 28 } }}>
            Backtest
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Paste an equity curve (CSV: date, equity) to analyze performance metrics and walk-forward windows.
          </Typography>

          <Box component="form" onSubmit={handleRun}>
            <Stack spacing={1.5} mb={2}>
              <TextField
                label="Equity Curve (CSV — date,equity)"
                multiline
                minRows={5}
                maxRows={12}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                size="small"
                inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
                helperText="One row per bar. Header row: date,equity"
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label="Benchmark Symbol"
                  value={benchmarkName}
                  onChange={(e) => setBenchmarkName(e.target.value.toUpperCase())}
                  size="small"
                  sx={{ width: 140 }}
                  helperText="Label only (e.g. SPY)"
                />
                <TextField
                  label="Walk-Forward Windows"
                  type="number"
                  value={windowCount}
                  onChange={(e) => setWindowCount(e.target.value)}
                  size="small"
                  sx={{ width: 140 }}
                  inputProps={{ min: 1, max: 20 }}
                  helperText="Number of OOS windows"
                />
                <TextField
                  label="Date Range"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  size="small"
                  sx={{ width: 140 }}
                  select
                  helperText="Filter imported/pasted rows"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="3m">Last 3M</MenuItem>
                  <MenuItem value="6m">Last 6M</MenuItem>
                  <MenuItem value="12m">Last 12M</MenuItem>
                </TextField>
                <Button variant="outlined" component="label" sx={{ minHeight: 40 }}>
                  Import CSV
                  <input
                    type="file"
                    accept=".csv,text/csv,.txt"
                    hidden
                    onChange={handleImportCsvFile}
                  />
                </Button>
                <Button type="submit" variant="contained" disabled={loading} sx={{ minHeight: 40 }}>
                  {loading ? <CircularProgress size={20} /> : "Run Backtest"}
                </Button>
              </Stack>
            </Stack>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

          {result && (
            <>
              {lowObservationWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Low observation count ({filteredRowsCount} filtered rows from {parsedRowsCount} total{minWindowObservations ? `, min window ${minWindowObservations}` : ""}).
                  Metrics may be unstable; use 30+ rows (preferably 60+) for more reliable backtest statistics.
                </Alert>
              )}
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab label="Summary" />
                <Tab label="Walk-Forward" />
                {comparison && <Tab label="vs Benchmark" />}
              </Tabs>

              {/* Summary */}
              {activeTab === 0 && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flexWrap: "wrap" }} mb={2}>
                    <MetricCard
                      label="Total Return"
                      value={pctFmt(totalReturn)}
                      highlight={totalReturn >= 0 ? "#388e3c" : "#d32f2f"}
                      tooltip="Total percentage return over the equity curve period"
                    />
                    <MetricCard
                      label="Max Drawdown"
                      value={maxDD !== null && maxDD !== undefined ? `-${fmt(maxDD)}%` : "--"}
                      highlight={maxDD > 20 ? "#d32f2f" : maxDD > 10 ? "#f57c00" : "#388e3c"}
                      tooltip="Peak-to-trough decline percentage"
                    />
                    <MetricCard
                      label="Sharpe Ratio"
                      value={fmt(sharpe, 2)}
                      highlight={sharpe >= 1.5 ? "#388e3c" : sharpe >= 0.5 ? "#1976d2" : "#d32f2f"}
                      tooltip="Risk-adjusted return (annualized)"
                    />
                    <MetricCard
                      label="Calmar Ratio"
                      value={fmt(calmar, 2)}
                      tooltip="Return / Max Drawdown"
                    />
                    <MetricCard
                      label="Win Rate"
                      value={winRate !== undefined ? `${fmt(winRate, 1)}%` : "--"}
                      tooltip="Percentage of periods with positive return"
                    />
                    <MetricCard
                      label="Annualized Volatility"
                      value={volatility !== undefined ? `${fmt(volatility, 1)}%` : "--"}
                      tooltip="Standard deviation of returns, annualized"
                    />
                  </Stack>
                  {Object.keys(metrics).length > 6 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="subtitle2" mb={1} color="text.secondary">
                        All Metrics
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }} useFlexGap>
                        {Object.entries(metrics).map(([k, v]) => (
                          <Chip
                            key={k}
                            size="small"
                            label={`${k}: ${typeof v === "number" ? fmt(v, 3) : String(v)}`}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                </>
              )}

              {/* Walk-Forward */}
              {activeTab === 1 && (
                <>
                  {windows.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Start</TableCell>
                            <TableCell>End</TableCell>
                            <TableCell align="right">Observations</TableCell>
                            <TableCell align="right">Return</TableCell>
                            <TableCell align="right">Sharpe</TableCell>
                            <TableCell align="right">Max DD</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {windows.map((w, i) => {
                            const windowReturnPct = w.oos_return_pct
                              ?? (w.metrics?.total_return !== undefined ? toPercent(w.metrics.total_return) : null);
                            const windowSharpe = w.oos_sharpe ?? w.metrics?.sharpe_ratio;
                            const windowDrawdownPct = w.oos_max_drawdown_pct
                              ?? (w.metrics?.max_drawdown !== undefined ? Math.abs(toPercent(w.metrics.max_drawdown) ?? 0) : null);
                            return (
                            <TableRow key={i} hover>
                              <TableCell>{w.window ?? i + 1}</TableCell>
                              <TableCell>{w.train_start ?? w.oos_start ?? w.start_date ?? "--"}</TableCell>
                              <TableCell>{w.train_end ?? w.oos_end ?? w.end_date ?? "--"}</TableCell>
                              <TableCell align="right">{w.observations ?? w.oos_observations ?? "--"}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{ color: (windowReturnPct ?? 0) >= 0 ? "success.main" : "error.main", fontWeight: 600 }}
                                >
                                  {pctFmt(windowReturnPct)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{fmt(windowSharpe, 2)}</TableCell>
                              <TableCell align="right">{windowDrawdownPct !== null && windowDrawdownPct !== undefined ? `-${fmt(windowDrawdownPct)}%` : "--"}</TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary">No walk-forward windows available.</Typography>
                  )}
                  {wfResult?.summary && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" mb={0.5} color="text.secondary">
                        Walk-Forward Summary
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }} useFlexGap>
                        {Object.entries(wfResult.summary).map(([k, v]) => (
                          <Chip
                            key={k}
                            size="small"
                            label={`${k}: ${typeof v === "number" ? fmt(v, 2) : String(v)}`}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}

              {/* vs Benchmark */}
              {activeTab === 2 && comparison && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ flexWrap: "wrap" }} mb={2}>
                  <MetricCard
                    label={`${benchmarkName} Return`}
                    value={pctFmt(comparisonBenchmarkReturnPct)}
                  />
                  <MetricCard
                    label="Strategy Return"
                    value={pctFmt(comparisonStrategyReturnPct)}
                  />
                  <MetricCard
                    label="Alpha"
                    value={pctFmt(comparisonAlphaPct)}
                    highlight={(comparisonAlphaPct ?? 0) >= 0 ? "#388e3c" : "#d32f2f"}
                    tooltip="Strategy return minus benchmark return"
                  />
                  <MetricCard
                    label="Sharpe Advantage"
                    value={fmt(comparisonSharpeDiff, 2)}
                    tooltip="Strategy Sharpe minus benchmark Sharpe"
                  />
                  <MetricCard
                    label="Drawdown Advantage"
                    value={pctFmt(comparisonDrawdownAdvantagePct)}
                    tooltip="Strategy max drawdown minus benchmark max drawdown"
                  />
                  <MetricCard
                    label="Excess CAGR"
                    value={pctFmt(comparisonCagrDiffPct)}
                    tooltip="Strategy CAGR minus benchmark CAGR"
                  />
                </Stack>
              )}

              <Divider sx={{ my: 2 }} />
              <Box>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowDebugPayload((prev) => !prev)}
                  sx={{ mb: 1 }}
                >
                  {showDebugPayload ? "Hide Debug Payload" : "Show Debug Payload"}
                </Button>
                {showDebugPayload && (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "grey.50" }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Parsed rows: {parsedRowsCount} | Filtered rows: {filteredRowsCount} ({dateRange.toUpperCase()}) | Run status: {result?.status ?? "--"} | Walk-forward status: {wfResult?.status ?? "--"}
                    </Typography>
                    <Typography variant="subtitle2" mb={0.5}>Run Payload</Typography>
                    <Box component="pre" sx={{ m: 0, maxHeight: 220, overflow: "auto", fontSize: 12 }}>
                      {prettyJson(result)}
                    </Box>
                    <Typography variant="subtitle2" mt={1.5} mb={0.5}>Walk-Forward Payload</Typography>
                    <Box component="pre" sx={{ m: 0, maxHeight: 220, overflow: "auto", fontSize: 12 }}>
                      {prettyJson(wfResult)}
                    </Box>
                  </Paper>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </ResponsiveContainer>
  );
};

export default BacktestView;
